/**
 * ContinuationRateEditor - Per-case expense continuation rate editor
 * 
 * Allows accountants to customize how much of each expense continues
 * during a forced business closure.
 */

import React, { useState, useEffect } from 'react';
import {
  Sliders,
  Save,
  RotateCcw,
  Info,
  ChevronDown,
  ChevronUp,
  Loader2,
  Check,
  Building2,
  AlertCircle
} from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

interface RateRange {
  min: number;
  max: number;
}

interface ContinuationRates {
  officer_compensation: RateRange;
  salaried_wages: RateRange;
  hourly_wages: RateRange;
  rent: RateRange;
  utilities: RateRange;
  insurance: RateRange;
  professional_services: RateRange;
  depreciation: RateRange;
  debt_service: RateRange;
  taxes_licenses: RateRange;
  other_expenses: RateRange;
}

interface ExpenseDescription {
  name: string;
  description: string;
  typical_range: string;
  notes: string;
}

interface ContinuationRateEditorProps {
  reportId: string;
  onSave?: () => void;
}

const EXPENSE_CATEGORIES = [
  { key: 'officer_compensation', group: 'Payroll' },
  { key: 'salaried_wages', group: 'Payroll' },
  { key: 'hourly_wages', group: 'Payroll' },
  { key: 'rent', group: 'Fixed Costs' },
  { key: 'utilities', group: 'Fixed Costs' },
  { key: 'insurance', group: 'Fixed Costs' },
  { key: 'professional_services', group: 'Variable' },
  { key: 'depreciation', group: 'Variable' },
  { key: 'debt_service', group: 'Variable' },
  { key: 'taxes_licenses', group: 'Variable' },
  { key: 'other_expenses', group: 'Other' },
];

export default function ContinuationRateEditor({ reportId, onSave }: ContinuationRateEditorProps) {
  const [rates, setRates] = useState<ContinuationRates | null>(null);
  const [descriptions, setDescriptions] = useState<Record<string, ExpenseDescription>>({});
  const [presets, setPresets] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isDefault, setIsDefault] = useState(true);
  const [notes, setNotes] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set(['Payroll', 'Fixed Costs']));
  const [showPresets, setShowPresets] = useState(false);

  useEffect(() => {
    loadRates();
    loadDefaults();
    loadPresets();
  }, [reportId]);

  const loadRates = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/api/v1/continuation-rates/${reportId}`);
      setRates(data.rates);
      setIsDefault(data.is_default);
      setNotes(data.notes || '');
    } catch (error) {
      console.error('Failed to load rates:', error);
      toast.error('Failed to load continuation rates');
    } finally {
      setLoading(false);
    }
  };

  const loadDefaults = async () => {
    try {
      const data = await api.get('/api/v1/continuation-rates/defaults/all');
      setDescriptions(data.descriptions);
    } catch (error) {
      console.error('Failed to load defaults:', error);
    }
  };

  const loadPresets = async () => {
    try {
      const data = await api.get('/api/v1/continuation-rates/presets/list');
      setPresets(data.presets);
    } catch (error) {
      console.error('Failed to load presets:', error);
    }
  };

  const handleRateChange = (key: string, field: 'min' | 'max', value: number) => {
    if (!rates) return;
    
    setRates(prev => {
      if (!prev) return prev;
      const updated = { ...prev };
      updated[key as keyof ContinuationRates] = {
        ...updated[key as keyof ContinuationRates],
        [field]: value
      };
      return updated;
    });
    setIsDefault(false);
  };

  const handleSave = async () => {
    if (!rates) return;
    
    try {
      setSaving(true);
      await api.put(`/api/v1/continuation-rates/${reportId}`, {
        rates,
        notes
      });
      toast.success('Continuation rates saved');
      setIsDefault(false);
      onSave?.();
    } catch (error) {
      console.error('Failed to save rates:', error);
      toast.error('Failed to save continuation rates');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setSaving(true);
      await api.delete(`/api/v1/continuation-rates/${reportId}`);
      await loadRates();
      toast.success('Rates reset to defaults');
    } catch (error) {
      console.error('Failed to reset rates:', error);
      toast.error('Failed to reset rates');
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (presetKey: string) => {
    const preset = presets[presetKey];
    if (!preset?.rates) return;
    
    setRates(preset.rates);
    setIsDefault(false);
    setShowPresets(false);
    toast.success(`Applied "${preset.name}" preset`);
  };

  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };

  const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!rates) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-gray-300" />
        <p>Unable to load continuation rates</p>
      </div>
    );
  }

  // Group expenses by category
  const groupedExpenses = EXPENSE_CATEGORIES.reduce((acc, { key, group }) => {
    if (!acc[group]) acc[group] = [];
    acc[group].push(key);
    return acc;
  }, {} as Record<string, string[]>);

  return (
    <div className="bg-white rounded-lg border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Sliders className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Continuation Rate Editor</h3>
              <p className="text-sm text-gray-500">
                {isDefault ? 'Using default rates' : 'Custom rates applied'}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowPresets(!showPresets)}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <Building2 className="w-4 h-4" />
              Presets
            </button>
            {!isDefault && (
              <button
                onClick={handleReset}
                disabled={saving}
                className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
                Reset
              </button>
            )}
            <button
              onClick={handleSave}
              disabled={saving || isDefault}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Save className="w-4 h-4" />
              )}
              Save
            </button>
          </div>
        </div>

        {/* Presets Dropdown */}
        {showPresets && Object.keys(presets).length > 0 && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm font-medium text-gray-700 mb-3">Apply Business Type Preset:</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(presets).map(([key, preset]: [string, any]) => (
                <button
                  key={key}
                  onClick={() => applyPreset(key)}
                  className="text-left px-3 py-2 bg-white border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-colors"
                >
                  <div className="font-medium text-sm text-gray-900">{preset.name}</div>
                  <div className="text-xs text-gray-500">{preset.description}</div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Rate Groups */}
      <div className="divide-y divide-gray-100">
        {Object.entries(groupedExpenses).map(([group, keys]) => (
          <div key={group}>
            {/* Group Header */}
            <button
              onClick={() => toggleGroup(group)}
              className="w-full flex items-center justify-between px-6 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
            >
              <span className="font-medium text-gray-700">{group}</span>
              {expandedGroups.has(group) ? (
                <ChevronUp className="w-4 h-4 text-gray-400" />
              ) : (
                <ChevronDown className="w-4 h-4 text-gray-400" />
              )}
            </button>

            {/* Group Items */}
            {expandedGroups.has(group) && (
              <div className="px-6 py-4 space-y-4">
                {keys.map((key) => {
                  const rate = rates[key as keyof ContinuationRates];
                  const desc = descriptions[key];
                  
                  return (
                    <div key={key} className="border border-gray-100 rounded-lg p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {desc?.name || key.replace(/_/g, ' ')}
                          </h4>
                          <p className="text-sm text-gray-500 mt-0.5">
                            {desc?.description || ''}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Info className="w-3 h-3" />
                          <span>Typical: {desc?.typical_range || 'N/A'}</span>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Min Rate */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Minimum Rate
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="300"
                              value={rate.min * 100}
                              onChange={(e) => handleRateChange(key, 'min', Number(e.target.value) / 100)}
                              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                            />
                            <span className="w-14 text-center text-sm font-medium text-gray-700 bg-gray-100 rounded px-2 py-1">
                              {formatPercent(rate.min)}
                            </span>
                          </div>
                        </div>

                        {/* Max Rate */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Maximum Rate
                          </label>
                          <div className="flex items-center gap-2">
                            <input
                              type="range"
                              min="0"
                              max="300"
                              value={rate.max * 100}
                              onChange={(e) => handleRateChange(key, 'max', Number(e.target.value) / 100)}
                              className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-blue-600"
                            />
                            <span className="w-14 text-center text-sm font-medium text-gray-700 bg-gray-100 rounded px-2 py-1">
                              {formatPercent(rate.max)}
                            </span>
                          </div>
                        </div>
                      </div>

                      {desc?.notes && (
                        <p className="mt-2 text-xs text-gray-400 italic">
                          {desc.notes}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Notes */}
      <div className="px-6 py-4 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes (optional)
        </label>
        <textarea
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setIsDefault(false);
          }}
          placeholder="Add notes about why these rates were adjusted..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          rows={2}
        />
      </div>
    </div>
  );
}
